import { useEffect, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Shield,
  Users,
  XCircle,
} from 'lucide-react'
import { meetingsAPI } from '../../services/api'

const SEVERITY_COLORS = {
  low: 'text-green-400',
  medium: 'text-yellow-400',
  high: 'text-red-400',
}

const SEVERITY_BG = {
  low: 'bg-green-900/30 border-green-700/50',
  medium: 'bg-yellow-900/30 border-yellow-700/50',
  high: 'bg-red-900/30 border-red-700/50',
}

const STATUS_ICONS = {
  unresolved: <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />,
  deferred: <Clock className="w-3.5 h-3.5 text-blue-400" />,
  blocked: <XCircle className="w-3.5 h-3.5 text-red-400" />,
}

export default function PreMeetingInsightsPanel({ meetingId, teamId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedSections, setExpandedSections] = useState({
    briefing: true,
    risks: true,
    topics: false,
    agenda: false,
    participants: false,
  })

  useEffect(() => {
    if (!meetingId) return
    setLoading(true)
    meetingsAPI
      .getPreMeetingIntelligence(meetingId, teamId)
      .then((res) => {
        setData(res.data ?? null)
        setError(null)
      })
      .catch((err) => {
        setError(err.message ?? 'Failed to load insights')
        setData(null)
      })
      .finally(() => setLoading(false))
  }, [meetingId, teamId])

  const toggleSection = (section) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading pre-meeting insights...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-zinc-500 text-sm py-4 text-center">
        {error ?? 'No pre-meeting insights available yet. Insights are generated after your first meeting.'}
      </div>
    )
  }

  const { preMeetingBriefing, meetingRiskAssessment, topicContinuity, agendaRecommendations, participantSummaries } =
    data

  const hasContent =
    preMeetingBriefing || meetingRiskAssessment || topicContinuity ||
    (agendaRecommendations && agendaRecommendations.length > 0) ||
    (participantSummaries && participantSummaries.length > 0)

  if (!hasContent) {
    return (
      <div className="text-zinc-500 text-sm py-4 text-center">
        No actionable insights from recent meetings.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Team Snapshot / Briefing */}
      {preMeetingBriefing && (
        <Section
          title="Team Briefing"
          icon={<Users className="w-4 h-4" />}
          expanded={expandedSections.briefing}
          onToggle={() => toggleSection('briefing')}
        >
          {preMeetingBriefing.team_snapshot && (
            <p className="text-zinc-300 text-sm mb-3">{preMeetingBriefing.team_snapshot}</p>
          )}

          {preMeetingBriefing.key_risks?.length > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-medium text-zinc-400 uppercase mb-1">Key Risks</h4>
              <ul className="space-y-1">
                {preMeetingBriefing.key_risks.map((risk, i) => (
                  <li key={i} className="text-sm text-yellow-300 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preMeetingBriefing.suggested_focus_areas?.length > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-medium text-zinc-400 uppercase mb-1">Focus Areas</h4>
              <ul className="space-y-1">
                {preMeetingBriefing.suggested_focus_areas.map((area, i) => (
                  <li key={i} className="text-sm text-blue-300 flex items-start gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preMeetingBriefing.follow_up_from_last_meeting?.length > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-medium text-zinc-400 uppercase mb-1">Follow-ups from Last Meeting</h4>
              <ul className="space-y-1">
                {preMeetingBriefing.follow_up_from_last_meeting.map((item, i) => (
                  <li key={i} className="text-sm text-zinc-300">
                    &bull; {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preMeetingBriefing.participant_notes?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-zinc-400 uppercase mb-1">Participant Notes</h4>
              <div className="space-y-2">
                {preMeetingBriefing.participant_notes.map((p, i) => (
                  <div key={i} className="bg-zinc-800/50 rounded px-3 py-2">
                    <span className="text-sm font-medium text-zinc-200">
                      {p.display_name || p.participant_id}
                    </span>
                    <p className="text-xs text-zinc-400 mt-0.5">{p.readiness_note}</p>
                    {p.relationship_context && (
                      <p className="text-xs text-zinc-500 mt-0.5 italic">{p.relationship_context}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Risk Assessment */}
      {meetingRiskAssessment && meetingRiskAssessment.risks?.length > 0 && (
        <Section
          title={`Risk Assessment — ${meetingRiskAssessment.overall_risk_level?.toUpperCase() ?? 'N/A'}`}
          icon={<Shield className="w-4 h-4" />}
          expanded={expandedSections.risks}
          onToggle={() => toggleSection('risks')}
          badgeColor={SEVERITY_COLORS[meetingRiskAssessment.overall_risk_level] ?? 'text-zinc-400'}
        >
          <div className="space-y-2">
            {meetingRiskAssessment.risks.map((risk, i) => (
              <div
                key={i}
                className={`rounded border px-3 py-2 ${SEVERITY_BG[risk.severity] ?? 'bg-zinc-800/50 border-zinc-700/50'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-zinc-200">{risk.category.replace(/_/g, ' ')}</span>
                  <span className={`text-xs uppercase ${SEVERITY_COLORS[risk.severity]}`}>{risk.severity}</span>
                </div>
                <p className="text-xs text-zinc-300">{risk.description}</p>
                {risk.mitigation_suggestion && (
                  <p className="text-xs text-zinc-400 mt-1">
                    <span className="text-zinc-500">Mitigation:</span> {risk.mitigation_suggestion}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Topic Continuity */}
      {topicContinuity && (topicContinuity.open_loops?.length > 0 || topicContinuity.resolved_topics?.length > 0) && (
        <Section
          title="Topic Continuity"
          icon={<Clock className="w-4 h-4" />}
          expanded={expandedSections.topics}
          onToggle={() => toggleSection('topics')}
        >
          {topicContinuity.open_loops?.length > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-medium text-zinc-400 uppercase mb-1">Open Loops</h4>
              <div className="space-y-2">
                {topicContinuity.open_loops.map((loop, i) => (
                  <div key={i} className="bg-zinc-800/50 rounded px-3 py-2">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {STATUS_ICONS[loop.status]}
                      <span className="text-sm text-zinc-200">{loop.topic}</span>
                    </div>
                    {loop.suggested_next_action && (
                      <p className="text-xs text-zinc-400 ml-5">Next: {loop.suggested_next_action}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {topicContinuity.resolved_topics?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-zinc-400 uppercase mb-1">Resolved</h4>
              <ul className="space-y-1">
                {topicContinuity.resolved_topics.map((topic, i) => (
                  <li key={i} className="text-sm text-green-400 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" />
                    {topic}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>
      )}

      {/* Agenda Recommendations */}
      {agendaRecommendations?.length > 0 && (
        <Section
          title="Suggested Agenda Items"
          icon={<CheckCircle className="w-4 h-4" />}
          expanded={expandedSections.agenda}
          onToggle={() => toggleSection('agenda')}
        >
          <div className="space-y-2">
            {agendaRecommendations.map((rec, i) => (
              <div key={i} className="bg-zinc-800/50 rounded px-3 py-2">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-zinc-200">
                    {rec.suggestedItemTitle || rec.suggested_item_title}
                  </span>
                  {rec.recommendedDurationMin && (
                    <span className="text-xs text-zinc-500">{rec.recommendedDurationMin} min</span>
                  )}
                </div>
                <p className="text-xs text-zinc-400">{rec.rationale}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      <p className="text-[11px] text-zinc-600 text-center pt-1">
        Based on meeting {data.sourceMeetingId?.slice(-8)} &middot; {new Date(data.computedAt).toLocaleDateString()}
      </p>
    </div>
  )
}

function Section({ title, icon, expanded, onToggle, badgeColor, children }) {
  return (
    <div className="bg-zinc-900/60 rounded-lg border border-zinc-800/70 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-800/40 transition-colors"
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        <span className={badgeColor ?? 'text-zinc-400'}>{icon}</span>
        <span className="text-sm font-medium text-zinc-200">{title}</span>
      </button>
      {expanded && <div className="px-4 pb-3">{children}</div>}
    </div>
  )
}
